/**
 * Sticky (pop-out) live-blog video player — pure logic.
 *
 * Pins the single-live-blog YouTube embed to the bottom-right of the viewport
 * while it is playing and its container is scrolled out of view. DOM wiring
 * lives in `src/live-blog-sticky-video-boot.js`; this module holds only
 * unit-testable logic: the iframe src param builder, YouTube postMessage
 * parse/serialize, and the pin/unpin state machine.
 *
 * Playback detection is YouTube's postMessage protocol (no iframe_api script):
 * with `enablejsapi=1` on the src the player emits `infoDelivery` events whose
 * `info.playerState` is 1 playing, 2 paused, 0 ended, 5 cued, 3 buffering.
 */

/** Player states that should keep the video visible: playing, buffering. */
const PLAYING_STATES = [1, 3];

/** Origins YouTube embeds postMessage from. */
const YT_ORIGINS = ["https://www.youtube.com", "https://www.youtube-nocookie.com"];

/**
 * Append `enablejsapi=1` to a YouTube embed src, preserving existing params
 * (same URL/URLSearchParams approach as `pauseOtherYouTubeEmbeds`).
 *
 * This is the ONLY src rewrite the feature ever performs and it must happen
 * pre-playback — rewriting src after playback starts reloads the iframe and
 * kills the video. Idempotent: a src that already carries the param (any
 * value) is returned untouched so no reload occurs.
 *
 * @param {string|null} src
 * @return {string|null} the rewritten src, or the input unchanged
 */
export function buildIframeSrc(src) {
	if (!src) return src;
	let url;
	try {
		url = new URL(src);
	} catch (e) {
		return src;
	}
	if (url.searchParams.has("enablejsapi")) return src;
	url.searchParams.set("enablejsapi", "1");
	return url.toString();
}

/**
 * Parse a `message` event from a YouTube embed into `{ event, playerState,
 * channel }`, or null when the event is not a trusted-shape infoDelivery
 * carrying a numeric playerState (wrong origin, non-JSON, other event types).
 *
 * YouTube postMessages `data` as a JSON string; already-parsed objects are
 * accepted too (handy for tests and synthetic events).
 *
 * @param {{origin?: string, data?: unknown}|null} event
 * @return {{event: string, playerState: number, channel?: string}|null}
 */
export function parsePlayerMessage(event) {
	if (!event || !YT_ORIGINS.includes(event.origin)) return null;
	let payload = event.data;
	if (typeof payload === "string") {
		try {
			payload = JSON.parse(payload);
		} catch (e) {
			return null;
		}
	}
	if (!payload || typeof payload !== "object") return null;
	if (payload.event !== "infoDelivery") return null;
	const playerState = payload.info && payload.info.playerState;
	if (typeof playerState !== "number") return null;
	return { event: payload.event, playerState, channel: payload.channel };
}

/**
 * Serialize the `listening` handshake posted to the iframe's contentWindow so
 * the player starts delivering infoDelivery events and accepts commands.
 *
 * @param {string} id
 * @param {string} channel
 * @return {string} JSON message
 */
export function serializeListening(id, channel) {
	return JSON.stringify({ event: "listening", id, channel });
}

/**
 * Serialize the `pauseVideo` command for the close button (no orphaned audio).
 * @return {string} JSON message
 */
export function serializePauseCommand() {
	return JSON.stringify({ event: "command", func: "pauseVideo", args: [] });
}

/**
 * Pin/unpin state machine.
 *
 * Pin iff the player is playing (playerState 1 or 3) AND the container is out
 * of view. Unpin when the container re-enters view, when the player leaves the
 * playing set (paused/ended/cued), or on dismiss. `dismiss()` unpins and
 * requests a `pauseVideo` (returns true); the machine re-arms naturally on the
 * next playing signal. If no play signal was ever received, nothing ever pins —
 * strict, no scroll-only fallback.
 *
 * @return {{playerState: Function, visibility: Function, dismiss: Function, pinned: boolean}}
 */
export function createPinMachine() {
	let playing = false; // last playerState ∈ {1, 3}
	let outOfView = false;
	let pinned = false;

	return {
		/** Feed an infoDelivery playerState; returns the new pinned state. */
		playerState(state) {
			playing = PLAYING_STATES.includes(state);
			if (!playing) pinned = false;
			else if (outOfView) pinned = true;
			return pinned;
		},
		/** Feed container visibility (true = at least partly in view). */
		visibility(inView) {
			outOfView = !inView;
			if (inView) pinned = false;
			else if (playing) pinned = true;
			return pinned;
		},
		/**
		 * Close button: unpin + request pauseVideo. Returns true when the
		 * caller should send the pause command (always). Re-arms on next play.
		 */
		dismiss() {
			pinned = false;
			playing = false;
			return true;
		},
		get pinned() {
			return pinned;
		},
	};
}
